/**
 * Manages password-related functionality for customer accounts including login, registration,
 * password reset, and session validation. Handles form validation, API interactions for
 * security checks, and user notifications.
 */
class PasswordManager {
    constructor() {
    }

    /**
     * Initializes the password manager by setting elements, events and validation parameters.
     */
    init() {
        this._setElements();
        this._setPasswordValidationParameters();
        this._initEvents();
        console.log('%c🔑 PasswordManager init', 'background: linear-gradient(90deg, #C8FE00, #f6f6f6); color: black; padding: 5px 10px; border-radius: 6px; font-weight: bold; font-family: monospace; font-size: 12px;');
    }

    /**
     * Finds and sets the main DOM elements used by the class.
     * @private
     */
    _setElements() {
        this.form = document.querySelector('form[data-password-manager-enabled="true"]');
        if (this.form) {
            this.passwordInput = this.form.querySelector('input[name="customer[password]"]');
            this.emailInput = this.form.querySelector('input[name="customer[email]"]');
            this.passwordConfirmationInput = this.form.querySelector('input[name="customer[password_confirmation]"]');
            this.submitButton = this.form.querySelector('[data-submit-button="true"]');
        }
    }

    /**
     * Initializes event listeners for the form and handles initial checks based on the request path.
     * @private
     */
    _initEvents() {
        if (this.form) {
            const formAction = this.form.action;
            if (formAction.includes('/account/login')) {
                this.checkLoginAttempts();
                this.emailInput?.addEventListener('change', this._saveLastEmail.bind(this));
                this.form.addEventListener('input', this.debounce(this.setCustomLoginValidation.bind(this), 300));
            } else if (formAction.includes('/account/reset')) {
                this._setLastEmail();
                this.emailInput?.addEventListener('change', this._saveLastEmailResetPage.bind(this));
                this.passwordInput?.addEventListener('input', this.debounce(this.validatePassword.bind(this), 300));
                this.passwordConfirmationInput?.addEventListener('input', this.debounce(this.validatePasswordConfirmation.bind(this), 300));
            } else {
                this.emailInput?.addEventListener('change', this._saveLastEmailResetPage.bind(this));
                this.passwordInput?.addEventListener('input', this.debounce(this.validatePassword.bind(this), 300));
            }
            this.form.addEventListener('input', this.debounce(this._validateForm.bind(this), 500));
        }
        this._checkSessionPassword();
    }

    /**
     * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
     * since the last time the debounced function was invoked.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The number of milliseconds to delay.  
     * @returns {Function} The debounced function.
     * @private
     */
    debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    /**
     * Saves the current email to a cookie and checks for password expiration.
     * @returns {Promise<void>}
     * @private
     */
    async _saveLastEmail() {
        console.log("_saveLastEmail");
        this.hideNotification();
        if (this.emailInput && this.emailInput.value !== '') {
            this._setCookie('last_email_password_manager', this.emailInput.value, 5);
            const response = await this._checkPasswordExpiration(this.emailInput.value);
            if (!response.has_history || response.expired || response.days_remaining === 0) {
                this.showNotification("Su contraseña ha expirado; por favor, proceda a cambiarla por la opción ¿Olvidaste tu contraseña?", 'warning', 0);
                this.submitButton.setAttribute('disabled', 'true');
            }
        }
    }

    /**
     * Saves the current email to a cookie on the reset password page.
     * @private
     */
    _saveLastEmailResetPage() {
        console.log("_saveLastEmailResetPage");
        if (this.emailInput) {
            this._setCookie('last_email_password_manager', this.emailInput.value, 5);
        }
    }

    /**
     * Retrieves the last saved email from cookies and sets it as the value for the email input.
     * @private
     */
    _setLastEmail() {
        const lastEmail = this._getCookie('last_email_password_manager');

        if (this.emailInput && lastEmail) {
            this.emailInput.value = lastEmail;
            this.emailInput.dispatchEvent(new Event('change'));
        }
    }

    /**
     * Sets custom validation for the login form.
     * @private
     */
    setCustomLoginValidation(e) {
        const input = e.target;

        if (input === this.passwordInput) {
            input.setCustomValidity('');

            if (input.validity.tooShort) {
                input.setCustomValidity(`La contraseña debe tener al menos ${window.passwordManagerSettings.passwordMinLength} caracteres.`);
            } else if (input.validity.tooLong) {
                input.setCustomValidity(`La contraseña no puede tener más de ${window.passwordManagerSettings.passwordMaxLength} caracteres.`);
            } else if (input.validity.patternMismatch) {
                input.setCustomValidity('La contraseña debe contener una mayúscula, una minúscula, un número y un carácter especial (@$!%*?&.-_#).');
            }
        }
    }

    /**
     * Retrieves the value of a cookie by its name.
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string|undefined} The value of the cookie if found, otherwise undefined.
     * @private
     */
    _getCookie(name) {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return undefined;
    }

    /**
     * Sets a cookie with a specific name, value, and validity period in minutes.
     * @param {string} name - The name of the cookie.
     * @param {string} value - The value of the cookie.
     * @param {number} minutes - The validity period in minutes.
     * @private
     */
    _setCookie(name, value, minutes) {
        const date = new Date();
        date.setTime(date.getTime() + (minutes * 60 * 1000));
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict;Secure`;
    }
    /**
     * Checks the number of login attempts from cookies and blocks the form if the limit is exceeded.
     */
    checkLoginAttempts() {
        const attempts = parseInt(this._getCookie('login_attempts') || '0');
        const maxAttempts = window.passwordManagerSettings.maxLoginAttempts;
        const lastEmail = this._getCookie('last_email_password_manager');

        if (attempts > maxAttempts) {
            console.log("Excedio el numero maximo de intentos fallidos");
            if (this.submitButton) {
                this.submitButton.setAttribute('disabled', 'true');
            }
            const inputs = this.form.querySelectorAll('input, button');
            inputs.forEach(input => input.setAttribute('disabled', 'true'));

            const unlockTime = parseInt(this._getCookie('login_unlock_time') || '0');
            let timeMessage = `${window.passwordManagerSettings.lockoutDurationMinutes} minutos`;

            if (unlockTime) {
                const now = new Date().getTime();
                const remainingMs = unlockTime - now;
                if (remainingMs > 0) {
                    const remainingMinutes = Math.ceil(remainingMs / 60000);
                    timeMessage = `${remainingMinutes} minuto${remainingMinutes !== 1 ? 's' : ''}`;
                }
            }

            this.submitButton.setAttribute('disabled', 'true');
            this.showNotification(`Inicio de sesión bloqueado temporalmente por intentos fallidos. Inténtelo de nuevo en ${timeMessage}.`, 'error', 0);

            if (this.emailInput && lastEmail) {
                this._setPasswordExpiration(lastEmail, 0);
            }
        }

        this._setLastEmail();
    }

    /**
     * Validates the form and executes the appropriate action based on the current request path.
     * @returns {Promise<void>}
     * @private
     */
    async _validateForm() {
        if (this.form.checkValidity()) {
            const { requestOrigin } = window.passwordManagerSettings;
            const formAction = this.form.action;
            const formData = new FormData(this.form);
            const formValues = Object.fromEntries(formData.entries());

            if (formAction === `${requestOrigin}/account/reset`) {
                await this._setNewPassword(formValues);
            } else if (formAction === `${requestOrigin}/account/login`) {
                await this._loginAttempt(formValues);
            } else if (formAction === `${requestOrigin}/account`) {
                await this._setNewPassword(formValues);
            } else if (formAction === `${requestOrigin}/account/edit-password`) {
                await this._setNewPassword(formValues);
            }
        }
    }

    /**
     * Checks if the session password validation has essentially occurred via token confirmation.
     * @returns {Promise<void>}
     * @private
     */
    async _checkSessionPassword() {
        const token = this._getCookie('confirmation_token');

        if (token) {
            const response = await this._confirmPassword(token);
            if (response.confirmed) {
                this._setCookie('confirmation_token', '', 0);
            }
            return;
        }

        if (window.passwordManagerSettings.isLoggedIn) {
            const response = await this._checkPasswordExpiration(window.passwordManagerSettings.customerEmail);

            if (response.expired && response.days_remaining === 0) {
                window.location.href = `${window.passwordManagerSettings.requestOrigin}/account/logout`;
            }
        }
    }

    /**
     * Handles the process of setting a new password, including API checks and notifications.
     * @param {Object} formValues - The values from the form.
     * @returns {Promise<void>}
     * @private
     */
    async _setNewPassword(formValues) {
        this.submitButton.setAttribute('disabled', 'true');
        const response = await this._passwordCheck(formValues['customer[email]'], formValues['customer[password]']);

        if (response instanceof Error) {
            this.showNotification("Error de conexión con el servidor, intente de nuevo", 'warning');
        } else if (response.valid === false) {
            if (response.code === 'VALIDATION_FAILED') {
                this.showNotification(response.errors?.password?.[0], 'warning', 0);
            } else if (response.code === 'PASSWORD_REUSED') {
                this.showNotification(response.message, 'warning', 0);
            }
        } else if (response.valid === true && response.code === 'PASSWORD_ACCEPTABLE') {
            this.showNotification(response.message, 'success', 0);
            document.cookie = `confirmation_token=${response.confirmation_token}; path=/; max-age=${response.expires_in}; secure; samesite=strict`;
            this.submitButton.removeAttribute('disabled');
        }
    }

    /**
     * Handles the login attempt logic specifically checking for password expiration.
     * @param {Object} formValues - The values from the form.
     * @returns {Promise<void>}
     * @private
     */
    async _loginAttempt(formValues) {
        const email = formValues.email || formValues['customer[email]'];
        const response = await this._checkPasswordExpiration(email);

        if (response instanceof Error) {
            this.showNotification("Error de conexión con el servidor, intente de nuevo", 'warning');
        } else {
            if (response.code === 'RATE_LIMIT_EXCEEDED') {
                const inputs = this.form.querySelectorAll('input, button');
                inputs.forEach(input => input.setAttribute('disabled', 'true'));
                this.showNotification(response.message, 'warning', 10000);
            } else if (response.expired === true || response.days_remaining === null) {
                this.showNotification("Su contraseña ha expirado; por favor, proceda a cambiarla por la opción ¿Olvidaste tu contraseña?", 'warning', 0);
            } else if (response.expired === false) {
                this.submitButton.removeAttribute('disabled');
            }
        }
    }

    /**
     * Sets the validation attributes (minlength, maxlength, pattern) for password inputs.
     */
    _setPasswordValidationParameters() {
        if (!window.passwordManagerSettings || !this.form) return;

        if (this.form.action.includes('/account/login')) return;

        if (this.passwordInput) {
            this.passwordInput.setAttribute('minlength', window.passwordManagerSettings.passwordMinLength);
            this.passwordInput.setAttribute('maxlength', window.passwordManagerSettings.passwordMaxLength);
            this.passwordInput.setAttribute('pattern', '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&\\.\\-_#])[A-Za-z\\d@$!%*?&\\.\\-_#]{8,20}$');
        }
        if (this.passwordConfirmationInput) {
            this.passwordConfirmationInput.setAttribute('minlength', window.passwordManagerSettings.passwordMinLength);
            this.passwordConfirmationInput.setAttribute('maxlength', window.passwordManagerSettings.passwordMaxLength);
            this.passwordConfirmationInput.setAttribute('pattern', '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&\\.\\-_#])[A-Za-z\\d@$!%*?&\\.\\-_#]{8,20}$');
        }
    }

    /**
     * Validates the main password input field showing custom validity messages.
     * @param {Event} event - The input event.
     */
    validatePassword(event) {
        console.log(this.emailInput.value);
        const { requestOrigin } = window.passwordManagerSettings;
        const formAction = this.form.action;
        if (formAction.includes('account/login')) {
            this.submitButton.removeAttribute('disabled');
            // Allow event to bubble to form for setCustomLoginValidation
            if (this.emailInput.value.length === 0) {
                this.hideNotification();
            }
            return;
        }

        event.target?.setCustomValidity('');

        if (event.target?.checkValidity() || event.target?.value.length === 0) {
            this.hideNotification();
            const { requestOrigin } = window.passwordManagerSettings;
            if (this.passwordConfirmationInput?.value.length > 0 && this.form.action.includes(`${requestOrigin}/account/reset`)) {
                this.validatePasswordConfirmation();
            }
            return;
        }

        let message = `<div class="password-manager-message">
            <p>La contraseña debe contener al menos:</p> 
            <ul>
                ${(event.target.validity.patternMismatch) ? `<li>Una mayúscula</li>` : ''}
                ${(event.target.validity.patternMismatch) ? `<li>Una minúscula</li>` : ''}
                ${(event.target.validity.patternMismatch) ? `<li>Un número</li>` : ''}
                ${(event.target.validity.patternMismatch) ? `<li>Un carácter especial (@$!%*?&.-_#)</li>` : ''}
                ${(event.target.validity.tooShort) ? `<li>Mínimo ${window.passwordManagerSettings.passwordMinLength} caracteres</li>` : ''}
                ${(event.target.validity.tooLong) ? `<li>Máximo ${window.passwordManagerSettings.passwordMaxLength} caracteres</li>` : ''}
            </ul>
            </div>`;

        event.target.setCustomValidity(message);
        this.showNotification(message, 'warning', 0);
    }

    /**
     * Validates the password confirmation input to ensure it matches the original password.
     * @param {Event} event - The input event.
     */
    validatePasswordConfirmation(event) {
        const input = this.passwordConfirmationInput;
        if (!input) return;

        input.setCustomValidity('');

        const passwordValue = this.passwordInput ? this.passwordInput.value : '';
        const confirmationValue = input.value;

        if (passwordValue !== confirmationValue) {
            const message = 'Las contraseñas no coinciden';
            input.setCustomValidity(message);
            this.showNotification(message, 'warning', 6000);
            return;
        }

        if (input.checkValidity() || input.value.length === 0) {
            this.hideNotification();
            return;
        }

        let message = `<div class="password-manager-message">
            <p>La contraseña debe contener al menos:</p> 
            <ul>
                ${(input.validity.patternMismatch) ? `<li>Una mayúscula</li>` : ''}
                ${(input.validity.patternMismatch) ? `<li>Una minúscula</li>` : ''}
                ${(input.validity.patternMismatch) ? `<li>Un número</li>` : ''}
                ${(input.validity.patternMismatch) ? `<li>Un carácter especial (@$!%*?&.-_#)</li>` : ''}
                ${(input.validity.tooShort) ? `<li>Mínimo ${window.passwordManagerSettings.passwordMinLength} caracteres</li>` : ''}
                ${(input.validity.tooLong) ? `<li>Máximo ${window.passwordManagerSettings.passwordMaxLength} caracteres</li>` : ''}
            </ul>
            </div>`;

        input.setCustomValidity(message);
        this.showNotification(message, 'warning');
    }

    /**
     * Calls the API to validate the password against strict rules.
     * @param {string} password - The password to validate.
     * @returns {Promise<Object>} The API response.
     * @private
     */
    async _validatePasswordBackend(password) {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Accept", "application/json");

        const raw = JSON.stringify({
            "store_domain": window.passwordManagerSettings.requestHost,
            "password": password,
            "min_length": window.passwordManagerSettings.passwordMinLength,
            "max_length": window.passwordManagerSettings.passwordMaxLength,
            "regex": "/^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&\\.\\-_#])[A-Za-z\\d@$!%*?&\\.\\-_#]{%d,%d}$/"
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${window.passwordManagerSettings.apiBaseUrl}api/password/validate-strict`, requestOptions);
            const result = await response.json();

            if (result instanceof Error || !result.valid) {
                this.showNotification(result.message, 'error', 0);
                return;
            } else {
                this.showNotification(result.message, 'success', 0);
                this.submitButton.removeAttribute('disabled');
            }
            return result;
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    /**
     * Calls the API to check if the password meets security requirements.
     * @param {string} email - The user's email.
     * @param {string} password - The password to check.
     * @returns {Promise<Object>} The API response.
     * @private
     */
    async _passwordCheck(email, password) {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "store_domain": window.passwordManagerSettings.requestHost,
            "email": email,
            "password": password,
            "expiration_days": window.passwordManagerSettings.passwordExpirationDays > 0 ? window.passwordManagerSettings.passwordExpirationDays : null,
            "history_limit": window.passwordManagerSettings.passwordHistoryCount > 0 ? window.passwordManagerSettings.passwordHistoryCount : null
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${window.passwordManagerSettings.apiBaseUrl}api/password/check`, requestOptions);
            const result = await response.json();
            return result;
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    /**
     * Calls the API to confirm a password using a confirmation token.
     * @param {string} token - The confirmation token.
     * @returns {Promise<Object>} The API response.
     * @private
     */
    async _confirmPassword(token) {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            "confirmation_token": token
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${window.passwordManagerSettings.apiBaseUrl}api/password/confirm`, requestOptions);
            const result = await response.json();
            return result;
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    /**
     * Calls the API to check if the user's password has expired.
     * @param {string} email - The user's email.
     * @returns {Promise<Object>} The API response.
     * @private
     */
    async _checkPasswordExpiration(email) {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

        const store_domain = window.passwordManagerSettings.requestHost;
        const url = `${window.passwordManagerSettings.apiBaseUrl}api/password/expiration?store_domain=${encodeURIComponent(store_domain)}&email=${encodeURIComponent(email)}`;

        const requestOptions = {
            method: "GET",
            headers: myHeaders,
            redirect: "follow"
        };

        try {
            const response = await fetch(url, requestOptions);
            const result = await response.json();
            return result;
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    /**
     * Calls the API to update the password expiration days.
     * @param {string} email - The user's email.
     * @param {number} expiration_days - The number of days for expiration.
     * @returns {Promise<Object>} The API response.
     * @private
     */
    async _setPasswordExpiration(email, expiration_days) {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Accept", "application/json");

        const raw = JSON.stringify({
            "email": email,
            "store_domain": window.passwordManagerSettings.requestHost,
            "expiration_days": expiration_days
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        try {
            const response = await fetch(`${window.passwordManagerSettings.apiBaseUrl}api/password/update-expiration`, requestOptions);
            const result = await response.json();
            return result;
        } catch (error) {
            console.error(error);
            return error;
        }
    }

    /**
     * Displays a notification to the user.
     * @param {string} text - The message to display.
     * @param {string} [variant='warning'] - The type of notification (e.g., 'warning', 'success').
     * @param {number} [duration=3000] - Duration in milliseconds to show the notification.
     */
    showNotification(text, variant = 'warning', duration = 3000) {
        // Handle 'error' variant by mapping to 'warning' since 'error' style doesn't exist
        const type = variant === 'error' ? 'warning' : variant;
        const getNotificationElement = () => document.getElementById('FormNotification') || document.getElementById('GlobalNotification');

        const notificationElement = getNotificationElement();

        if (notificationElement && customElements.get('notification-form-component')) {
            notificationElement.show(text, type, duration);
        } else {
            customElements.whenDefined('notification-form-component').then(() => {
                const notification = getNotificationElement();
                if (notification) notification.show(text, type, duration);
            });
        }
    }

    /**
     * Hides the global or form notification if it is currently visible.
     */
    hideNotification() {
        const notificationElement = document.getElementById('FormNotification');
        if (notificationElement && customElements.get('notification-form-component')) {
            notificationElement.hide();
        }
    }
}

const passwordManagerInstance = new PasswordManager().init();

/**
 * Notification component class.
 * @extends HTMLElement
 */
class NotificationComponent extends HTMLElement {
    constructor() {
        super();
        this._timer = null;
        this._banner = null;
    }

    /**
     * Called when the element is added to the DOM. Initializes the banner container.
     */
    connectedCallback() {
        // Ensure the container is ready
        if (!this._banner) {
            this._banner = document.createElement('div');
            this._banner.className = 'notification-banner';
            this._banner.setAttribute('role', 'alert');
            this.appendChild(this._banner);
        }
    }

    /**
     * Show a notification message.
     * @param {string} message - The message to display.
     * @param {string} type - 'success' or 'warning'.
     * @param {number} duration - Time in ms before auto-hiding.
     */
    show(message, type = 'success', duration = 3000) {
        // Clear any existing timer
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }

        // Ensure banner exists (in case called before connectedCallback)
        if (!this._banner) {
            this.connectedCallback();
        }

        // Reset classes to base
        this._banner.className = 'notification-banner';

        // Add variant class
        if (type === 'success' || type === 'warning') {
            this._banner.classList.add(type);
        }

        // Set content
        this._banner.innerHTML = '';
        if (message instanceof Node) {
            this._banner.appendChild(message);
        } else {
            this._banner.innerHTML = message;
        }

        // Add visible class next frame to ensure transition plays if it was hidden
        requestAnimationFrame(() => {
            this._banner.classList.add('visible');
        });

        // Set auto-hide timer
        if (duration > 0) {
            this._timer = setTimeout(() => {
                this.hide();
            }, duration);
        }
    }

    /**
     * Hides the notification banner.
     */
    hide() {
        if (this._banner) {
            this._banner.classList.remove('visible');
        }
    }
}

// Define the custom element
if (!customElements.get('notification-form-component')) {
    customElements.define('notification-form-component', NotificationComponent);
}

